<template>
  <el-dialog
    :title="isEditing ? 'Edit Question' : 'Add Question'"
    :visible.sync="dialogVisible"
    width="600px"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <el-form
      ref="questionForm"
      :model="form"
      :rules="rules"
      label-position="top"
    >
      <el-form-item label="Question" prop="question">
        <el-input
          type="textarea"
          v-model="form.question"
          placeholder="Enter your question..."
          :autosize="{ minRows: 3, maxRows: 6 }"
          maxlength="500"
          show-word-limit
        />
      </el-form-item>

      <el-form-item label="Options" prop="options">
        <div
          v-for="(option, index) in form.options"
          :key="index"
          class="option-input"
        >
          <el-input
            v-model="form.options[index]"
            :placeholder="`Option ${optionLabels[index]}`"
            maxlength="200"
          >
            <template slot="prepend">{{ optionLabels[index] }}.</template>
          </el-input>
        </div>
      </el-form-item>

      <el-form-item label="Correct Answer" prop="correct_answer">
        <el-radio-group v-model="form.correct_answer">
          <el-radio-button
            v-for="(option, index) in form.options"
            :key="index"
            :label="optionLabels[index]"
            :disabled="!option || option.trim() === ''"
          >
            {{ optionLabels[index] }}
          </el-radio-button>
        </el-radio-group>
      </el-form-item>

      <el-form-item label="Explanation" prop="explanation">
        <el-input
          type="textarea"
          v-model="form.explanation"
          placeholder="Explain the correct answer..."
          :autosize="{ minRows: 3, maxRows: 6 }"
          maxlength="1000"
          show-word-limit
        />
      </el-form-item>

      <el-row :gutter="20">
        <el-col :span="8">
          <el-form-item label="Pool" prop="question_kind">
            <el-select v-model="form.question_kind" style="width: 100%;">
              <el-option label="Comprehension" value="comprehension" />
              <el-option label="Application" value="application" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Difficulty" prop="difficulty">
            <el-select v-model="form.difficulty" style="width: 100%;">
              <el-option label="Easy" value="easy" />
              <el-option label="Medium" value="medium" />
              <el-option label="Hard" value="hard" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="Status" prop="status">
            <el-select v-model="form.status" style="width: 100%;">
              <el-option label="Active" value="active" />
              <el-option label="Draft" value="draft" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="Tags">
        <el-select
          v-model="form.tags"
          multiple
          filterable
          allow-create
          default-first-option
          placeholder="Add tags..."
          style="width: 100%;"
        >
          <el-option
            v-for="tag in suggestedTags"
            :key="tag"
            :label="tag"
            :value="tag"
          />
        </el-select>
      </el-form-item>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <el-button type="primary" @click="handleSave" :loading="saving">
        {{ isEditing ? 'Update' : 'Create' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { postRequest } from '@/apis/front'

export default {
  name: 'TestBankQuestionEditor',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    question: {
      type: Object,
      default: () => null
    },
    collectionId: {
      type: String,
      required: true
    }
  },
  data() {
    const validateOptions = (rule, value, callback) => {
      const filledOptions = value.filter(opt => opt && opt.trim())
      if (filledOptions.length < 2) {
        callback(new Error('At least 2 options are required'))
      } else {
        callback()
      }
    }

    return {
      dialogVisible: false,
      saving: false,
      optionLabels: ['A', 'B', 'C', 'D'],
      suggestedTags: ['vocabulary', 'grammar', 'reading', 'listening', 'writing'],
      form: {
        question: '',
        options: ['', '', '', ''],
        correct_answer: '',
        explanation: '',
        difficulty: 'medium',
        question_kind: 'comprehension',
        status: 'active',
        tags: []
      },
      rules: {
        question: [
          { required: true, message: 'Question is required', trigger: 'blur' }
        ],
        options: [
          { validator: validateOptions, trigger: 'blur' }
        ],
        correct_answer: [
          { required: true, message: 'Please select the correct answer', trigger: 'change' }
        ],
        difficulty: [
          { required: true, message: 'Please select difficulty', trigger: 'change' }
        ],
        question_kind: [
          { required: true, message: 'Please select pool', trigger: 'change' }
        ]
      }
    }
  },
  computed: {
    isEditing() {
      return this.question !== null
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.initForm()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    initForm() {
      if (this.question) {
        this.form = {
          question: this.question.question || '',
          options: this.question.options ? [...this.question.options] : ['', '', '', ''],
          correct_answer: this.question.correct_answer || '',
          explanation: this.question.explanation || '',
          difficulty: this.question.difficulty || 'medium',
          question_kind: this.question.question_kind || 'comprehension',
          status: this.question.status || 'active',
          tags: this.question.tags ? [...this.question.tags] : []
        }
        // Ensure we have 4 options
        while (this.form.options.length < 4) {
          this.form.options.push('')
        }
      } else {
        this.resetForm()
      }
    },

    resetForm() {
      this.form = {
        question: '',
        options: ['', '', '', ''],
        correct_answer: '',
        explanation: '',
        difficulty: 'medium',
        question_kind: 'comprehension',
        status: 'active',
        tags: []
      }
      this.$nextTick(() => {
        this.$refs.questionForm?.clearValidate()
      })
    },

    handleClose() {
      this.dialogVisible = false
      this.resetForm()
    },

    async handleSave() {
      try {
        await this.$refs.questionForm.validate()
      } catch (e) {
        return
      }

      this.saving = true
      try {
        const questionId = this.question?._id?.$oid || this.question?._id

        const response = await postRequest('updateTestBankQuestion', {
          question_id: questionId || null,
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id,
          question: this.form.question,
          options: this.form.options.filter(opt => opt && opt.trim()),
          correct_answer: this.form.correct_answer,
          explanation: this.form.explanation,
          difficulty: this.form.difficulty,
          question_kind: this.form.question_kind,
          status: this.form.status,
          tags: this.form.tags,
          generation_source: this.isEditing ? (this.question.generation_source || 'manual') : 'manual'
        })

        if (response.data.code === 0) {
          this.$message.success(this.isEditing ? 'Question updated' : 'Question created')
          this.$emit('save')
          this.handleClose()
        } else {
          this.$message.error(response.data.msg || 'Failed to save question')
        }
      } catch (error) {
        console.error('Error saving question:', error)
        this.$message.error('Failed to save question')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.option-input {
  margin-bottom: 10px;
}

.option-input:last-child {
  margin-bottom: 0;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
</style>
